import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useFormContext } from "react-hook-form";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import {
  type IActivityGroupRegisteredRetroactively,
  type IActivityRegisteredRetroactively,
  fetchPatientsActivityOccurrencesAndGroups,
  registerActivitiesRetroactivelyBulk,
  registerActivityRetroactively,
} from "@/api/Activities";
import {
  saveMultipleActivitiesInvoicingCodes,
  shouldSaveInvoice,
} from "@/api/Invoicing";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import { isGroup } from "@medoma/models/activities";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import type { IPatientId } from "@medoma/models/patients";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import type { ActivityRowFields } from "./activityRow";
import type { IRegisterActivitiesRetroactivelyFormData } from "./RegisterActivityRetroactivelyForm";

export type Submission =
  | { kind: "group"; group: IActivityGroupRegisteredRetroactively }
  | { kind: "single"; activity: IActivityRegisteredRetroactively };

// GVR (the invoicing system) rejects two activities with the exact same
// finishedAt for the same patient, so we block the submit up-front when the
// date + time collides with an existing occurrence.
class RetroactiveRegistrationConflictError extends Error {}

const hasExistingRegistrationAtSameTime = async ({
  patientId,
  submission,
}: {
  patientId: IPatientId;
  submission: Submission;
}) => {
  const { date, time } =
    submission.kind === "group" ? submission.group : submission.activity;
  const formattedDate = calendarDateSchema.encode(date);

  const occurrencesAndGroups = await fetchPatientsActivityOccurrencesAndGroups(
    patientId,
    formattedDate,
    formattedDate,
  );
  return occurrencesAndGroups
    .flatMap((occurrenceOrGroup) =>
      isGroup(occurrenceOrGroup)
        ? occurrenceOrGroup.occurrences
        : [occurrenceOrGroup],
    )
    .some(
      ({ finishedAt }) =>
        finishedAt &&
        machineFormat(finishedAt, "yyyy-MM-dd") === formattedDate &&
        machineFormat(finishedAt, "HH:mm") === time,
    );
};

export const useSubmitRetroactively = ({
  patientId,
}: {
  patientId: IPatientId;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organization } = useUserProfile();
  const { alert } = useDialog();
  const { setError, reset, getValues } =
    useFormContext<IRegisterActivitiesRetroactivelyFormData>();

  return useMutation({
    mutationFn: async ({
      submission,
      activities,
    }: {
      submission: Submission;
      activities: ActivityRowFields[];
    }) => {
      const invoicingActivated = organizationUsesInvoicingCodes(organization);

      if (
        invoicingActivated &&
        (await hasExistingRegistrationAtSameTime({
          patientId,
          submission,
        }))
      ) {
        throw new RetroactiveRegistrationConflictError();
      }

      if (submission.kind === "group") {
        await registerActivitiesRetroactivelyBulk(submission.group);
      } else {
        await registerActivityRetroactively(submission.activity);
      }

      const category =
        submission.kind === "group"
          ? submission.group.category
          : submission.activity.category;

      const invoicingCodesByActivity = activities
        .filter((activity) =>
          shouldSaveInvoice({
            invoicingActivated,
            category,
            procedureCodes: activity.procedureCodes,
            productCodes: activity.productCodes,
            activityHasPatient: true,
          }),
        )
        .map((activity) => ({
          activityId: activity.activityId,
          procedureCodes: activity.procedureCodes,
          productCodes: activity.productCodes,
          patientId,
        }));

      if (invoicingCodesByActivity.length > 0) {
        await saveMultipleActivitiesInvoicingCodes(invoicingCodesByActivity);
      }
    },
    onError: (error) => {
      if (error instanceof RetroactiveRegistrationConflictError) {
        alert({
          title: t`Kan inte efterregistrera`,
          message: t`Ett besök har redan registrerats med samma sluttid. GVR tillåter inte att flera besök har samma sluttid – ändra tiden och försök igen. Det går att ändra på ett registrerat besök tills det är skickat till GVR.`,
        });
        return;
      }

      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      void navigate("../..");
    },
  });
};
